import AssetActionsCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/asset-actions';
import AssetStatusCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/asset-status';
import DateCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/date-cell';
import DateTimeCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/date-time-cell';
import LinkCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/link-cell';
import AssetTypeCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-type-cell';
import { TextCell } from '@nile/rmm/src/modules/scripts/components/cells';
import { ActionCell } from '@nile/rmm/src/modules/alert-management/components/sub-pages/action-cell';
import { TRASH_ASSET } from '@nile/rmm/src/modules/common/constants';
import { getTranslatedText } from '@nile/components';
import { isEmpty, cloneDeep, map } from 'lodash';
import {
  pageSize,
  disableUnselectColumn
} from '@nile/rmm/src/modules/asset-management/constants/asset-list';
import MaskedCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/masked-cell';
import AssetAge from '@nile/rmm/src/modules/alert-management/components/sub-pages/asset-age';
import { CustomSecureTextCell } from '@nile/mangifera/src/Components/Ticket';
import ActionPopover from '@nile/banyan/src/Pages/Protected/Configuration/Pricing/Cells/ActionPopover';
import AssetPatchStatusCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-patch-status-cell/AssetPatchStatusCell';
import MonitorSupportCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/monitor-support-cell';
import ActivateUnlicensedAssetCell from '@nile/rmm/src/modules/asset-management/components/sub-pages/unlicensed-asset-cell';

const columnsConfig = {
  minSize: 30,
  maxSize: 500,
  size: 300,
  minWidth: 30,
  maxWidth: 500,
  width: 300,
  TEXT: TextCell,
  LINK: LinkCell,
  STATUS: AssetStatusCell,
  DATE: DateCell,
  DATETIME: DateTimeCell,
  ACTIONS: AssetActionsCell,
  TYPE: AssetTypeCell,
  BUTTON_ACTION: ActionCell,
  MASKED: MaskedCell,
  AGE: AssetAge,
  SecureText: CustomSecureTextCell,
  ASSETPATCHSTATUS: AssetPatchStatusCell,
  MONITOR_SUPPORT: MonitorSupportCell,
  UNLICENSE_ACTIVATE: ActivateUnlicensedAssetCell
};

const getAttributeName = (column) => {
  switch (column?.name) {
    case 'name':
      return 'name';
    case 'client':
      return 'client.name';
    case 'site':
      return 'site.name';
    default:
      return column?.name;
  }
};

const getCategory = (source, clientPortal = false) => {
  switch (source) {
    case 'ASSET_GROUP':
      return 'AssetGroup';
    default:
      return clientPortal ? 'PortalAssetView' : 'AssetView';
  }
};

const getColumnWidth = (colName, isMinWidth = false) => {
  switch (colName) {
    case 'platform':
      return isMinWidth ? 50 : 300;
    case 'lastReportedTime':
      return 250;
    case 'id':
      return 65;
    default:
      return isMinWidth ? 50 : 180;
  }
};

const getColumnAccessor = (col) => {
  if (col.isCustom) {
    return `customFields.${col.name}`;
  }
  switch (col.name) {
    case 'name':
      return 'name';
    case 'client':
      return 'client.name';
    case 'site':
      return 'site.name';
    default:
      return col.name;
  }
};

const getColumnCellType = (
  col,
  activeView,
  isAssetInClientPortal,
  scriptView
) => {
  if (col.name.includes('date')) {
    return 'DATE';
  }
  if (col.uiDataType === 'date') {
    return 'DATE';
  }
  if (col.name.includes('dt')) {
    return 'DATETIME';
  }
  if (col.uiDataType === 'datetime') {
    return 'DATETIME';
  }
  if (col.uiDataType === 'password') {
    return 'MASKED';
  }
  switch (col.uiDataType) {
    case 'secure_text':
      return 'SecureText';
    default:
  }
  switch (col.name) {
    case 'name':
      if (
        activeView?.systemViewProperty === TRASH_ASSET ||
        isAssetInClientPortal ||
        scriptView
      ) {
        return 'TEXT';
      }
      return 'LINK';

    case 'status':
      return 'STATUS';
    case 'id':
      return 'TYPE';
    case 'assetAge':
      return 'AGE';
    case 'patchStatus':
      return 'ASSETPATCHSTATUS';
    case 'monitoringStatus':
      return 'MONITOR_SUPPORT';
    case 'assetActivate':
      return 'UNLICENSE_ACTIVATE';
    default:
      return 'TEXT';
  }
};

const getFieldsToBeRequested = (
  columns,
  isAssetInClientPortal = false,
  isAssetPatchReport
) => {
  const customFields = columns.filter((column) => {
    return column.isCustom === true;
  });
  let fieldsToRequest = [
    {
      listInfo: ['page', 'pageSize', 'hasMore', 'totalCount', 'condition']
    }
  ];

  fieldsToRequest[0].listInfo.push({
    sort: ['attribute', 'order']
  });

  const allFields = [];

  if (customFields?.length > 0) {
    allFields.push(`customFields(
      fields: ${JSON.stringify(map(customFields, 'name'))},
    )`);
  }
  const clientFields = ['name'];
  if (isAssetInClientPortal) {
    clientFields.push('mappedToWFH');
  }

  allFields.push({
    client: clientFields
  });
  allFields.push('name');
  allFields.push('assetId');
  allFields.push('loggedInUser');
  allFields.push({
    assetClass: ['classId']
  });
  allFields.push({
    site: ['name']
  });
  allFields.push({
    owner: ['name']
  });
  allFields.push({
    department: ['businessFunctionId', 'name']
  });

  const workStationFields = [
    '__typename',
    'serialNumber',
    'manufacturer',
    'model',
    'hostName',
    'primaryIp',
    'gateway',
    'platform',
    'domain',
    'status',
    'tag',
    'sysUptime',
    'teamViewerIntegrated',
    'splashtopIntegrated',
    'islIntegrated',
    'cwcIntegrated',
    'primaryMac',
    'platformFamily',
    'platformVersion',
    'platformCategory',
    'agentVersion',
    'assetAge',
    'lastReportedTime',
    'patchStatus',
    'bitLockerStatus',
    'rebootNeeded',
    'rebootCause',
    'warrantyExpiryDate',
    'purchasedDate',
    'registeredTime',
    'client { accountId }',
    'site { id }',
    'serialKey',
    'biosVersion',
    'cpuMemData { cpuName, architecture, totalMemoryV2 }'
  ];

  if (!isAssetPatchReport) {
    workStationFields.push('remoteDesktopEnabledStatus');
    workStationFields.push('policyCategory { name }');
  }

  allFields.push(`... on WorkStation {
            ${workStationFields.join('\n        ')}
          }`);

  fieldsToRequest.push({
    assets: allFields
  });

  return fieldsToRequest;
};

const getNetworkFieldsToBeRequested = (
  columns,
  isAssetInClientPortal = false
) => {
  const customFields = columns.filter((column) => {
    return column.isCustom === true;
  });
  let fieldsToRequest = [
    {
      listInfo: ['page', 'pageSize', 'hasMore', 'totalCount', 'condition']
    }
  ];

  fieldsToRequest[0].listInfo.push({
    sort: ['attribute', 'order']
  });

  const allFields = [];

  if (customFields?.length > 0) {
    allFields.push(`customFields(
      fields: ${JSON.stringify(map(customFields, 'name'))},
    )`);
  }
  const clientFields = ['name'];

  allFields.push({
    client: clientFields
  });
  allFields.push('name');
  allFields.push('assetId');
  allFields.push({
    assetClass: ['classId', 'name']
  });
  allFields.push({
    site: ['name']
  });
  allFields.push({
    owner: ['name']
  });

  allFields.push(`... on IPAsset {
        __typename
        status
        ipAddress
        macAddress
        publicIpAddress
        serialNumber
        hostName
        sysUptime
        assetMake {
          makeId
          name
        }
        assetModel {
          modelId
          name
        }
      }`);

  fieldsToRequest.push({
    assets: allFields
  });

  return fieldsToRequest;
};

const getAssetGroupVariables = (activePage) => {
  const variables = {
    groupId: {
      type: 'ID',
      required: true
    },
    listInfo: {
      pageSize,
      page: activePage,
      type: 'ListInfoInput',
      required: true
    }
  };
  return variables;
};

const getAssetClassVariables = (activePage) => {
  const variables = {
    listInfo: {
      pageSize,
      page: activePage,
      type: 'ListInfoInput',
      required: true
    }
  };
  return variables;
};

const getAssetClassReportVariables = (activePage) => {
  const variables = {
    chartModule: {
      type: 'String',
      required: true
    },
    metricId: {
      type: 'ID',
      required: true
    },
    listInfo: {
      pageSize,
      page: activePage,
      type: 'ListInfoInput',
      required: true
    }
  };
  return variables;
};

export {
  columnsConfig,
  getAttributeName,
  getCategory,
  getColumnWidth,
  getColumnAccessor,
  getColumnCellType,
  getFieldsToBeRequested,
  getAssetClassVariables,
  getAssetGroupVariables,
  getAssetClassReportVariables,
  getNetworkFieldsToBeRequested
};
